
export class SlotMath
{
    /**
     * Constructs math object.
     * @class
     * @constructor
     * @param {Object} config Description object
     * @name SlotMath
     * @classdesc This is a core library class aimed to detect win symbols combination in barrel symbols sequences.
     * @tutorial 1.10-adding-slotmath
     * */
    constructor( config )
    {
        this._lines = config.lines;
        this._symbols = config.symbols;
        this._lineTrajectories = config.trajectories;

        this._reels = config.reels || 5;
        this._rows = config.rows || 3;

        this._linePatterns = config.winShapes || [
                [1,1,1,1,1],
                [1,1,1,1,0],
                [1,1,1,0,0],
                [1,1,0,0,0]
            ];

        this._price = config.price;
        this._scatterSymbolPrice = config.scatterPrice;

        this._wildSymbol = config.wild != null ? config.wild : -1;
        this._scatterSymbol = config.scatter != null ? config.scatter : -1;
        this._bonusSymbol = config.bonus != null ? config.bonus : -1;

        this._bonusWinCondition = (config.bonusWinCondition !== undefined) ? config.bonusWinCondition : 6;

        this._wildSymbolLineWinMultiplier = config.wildLineWinMultiplier || 1;

        this._substitutableElements = config.substitutableElements || (function () {
                var arr = [];
                for (var i = 0; i < config.symbols; ++i) {
                    if (i === this._wildSymbol || i === this._bonusSymbol || i === this._scatterSymbol) continue;

                    arr.push(i);
                }

                return arr;
            }.bind(this))();

        this._winMultiplier = 1;

        this._linesWin = [];
        this._linesWinPositions = [];

        this._lineWinSymbols = []; // symbol which wins on line

        this._scatterSymbolCount = 0;
        this._scatterSymbolPositions = [];

        this._bonusSymbolCount = 0;
        this._bonusSymbolPositions = [];


        /**
         * Actual lines number.
         * @private
         * @memberOf SlotMath
         * @type {Number}
         */
        this._actualLines = 0;

        /**
         * Actual multiplier.
         * @private
         * @memberOf SlotMath
         * @type {Number}
         */
        this._actualMultiplier = 0;

        this._combination = [];
    }

    /**
    * Get max lines value.
    * @returns {Number} Max lines number
    * @memberOf SlotMath
    * */
    get lines() {
        return this._lines
    }

    /**
    * Get wild symbol code.
    * @returns {Number} Wild symbol code
    * @memberOf SlotMath
    * */
    get wild() {
        return this._wildSymbol
    }

    /**
    * Get scatter symbol code.
    * @returns {Number} Scatter symbol code
    * @memberOf SlotMath
    * */
    get scatter() {
        return this._scatterSymbol
    }

    /**
    * Get bonus symbol code.
    * @returns {Number} Bonus symbol code
    * @memberOf SlotMath
    * */
    get bonus() {
        return this._bonusSymbol
    }

    /**
     * Returns max available symbols number.
     * @returns {Number} Symbols number
     * @memberOf SlotMath
     * */
    symbolsNumber() {
        return this._symbols
    }

    /**
     * Returns max available lines number.
     * @returns {Number} Maximum lines number
     * @memberOf SlotMath
     * */
    linesNumber() {
        return this._lines
    }

    /**
     * Returns actual lines number.
     * @returns {Number} Actual lines number
     * @memberOf SlotMath
     * */
    actualLinesNumber() {
        return this._actualLines
    }

    /**
     * Returns chained symbols price.
     * @param {Number} symbol Symbol code
     * @param {Number} times Chain length
     * @returns {Number} Price (derived to bet multiplier equals 1)
     * @memberOf SlotMath
     * */
    price(symbol, times) {
        if (symbol == this._scatterSymbol) {
            return this._scatterSymbolPrice[times];
        }

        return this._price[symbol][times];
    }

    /**
     * Returns line win.
     * @param {Number} line Line index
     * @returns {Number} Line win
      * @memberOf SlotMath
     * */
    lineWin( line ) {
        return this._linesWin[line] * this._actualMultiplier * this._winMultiplier;
    }

    /**
     * Returns accumulative lines win.
     * @returns {Number} The accumulative line's win
     * @memberOf SlotMath
     * */
    linesWin() {
        var summ = 0;
        for (var i = 0; i < this._actualLines; ++i) {
            summ += this.lineWin(i);
        }

        return summ;
    }
    
    /**
     * Returns array of win lines indexes.
     * @returns {Number[]} The array of win lines indexes
     * @memberOf SlotMath
     * */
    winLines() {
        var lines = [];
        for (var i = 0; i < this._actualLines; ++i) {
            if (this.lineWin(i) > 0) {
                lines.push(i);
            }
        }
        return lines;
    }

    /**
     * Returns array of win positions on line.
     * @param {Number} line Line index
     * @returns {Number[]} Line win positions
     * @memberOf SlotMath
     * */
    lineWinPositions( line ) {
        return this._linesWinPositions[line];
    };


    /**
     * Returns symbol which wins on line
     * @param {Number} line Line index
     * @returns {Number} win symbol code
     * @memberOf SlotMath
     * */
    lineWinSymbol(line) {
        return this._lineWinSymbols[line];
    }
    
    
    /**
     * Returns array of scatter symbols win positions on barrel.
     * @returns {Number[]} Scatter win positions
     * @memberOf SlotMath
     * */
    scatterPositions() {
        return this._scatterSymbolPositions;
    }
    
    reel_has_scatter( symbols, reel ) {
        for ( let i = reel * 3, max = ( reel + 1 ) * 3; i < max; ++i ) {
            if ( symbols[ i ] == this.scatter ) return true
        }
        return false
    }

    /**
     * Returns scatter count (whenever scatters win or not).
     * @returns {Number} Scatter count
     * @memberOf SlotMath
     * */
    scatterCount() {
        return this._scatterSymbolCount > this._reels ? this._reels : this._scatterSymbolCount;
    }

    
    /**
     * Returns scatter win.
     * @returns {Number} Scatter win in credits
     * @memberOf SlotMath
     * */
    scatterWin() {
        if (this._scatterSymbol >= 0) {
            return this.price(this._scatterSymbol, this.scatterCount()) * this._actualMultiplier * this._actualLines * this._winMultiplier;
        }

        return 0;
    }


    /**
     * Returns bonus symbols count (whenever bonus fired or not).
     * @returns {Number} bonus symbols count
     * @memberOf SlotMath
     * */
    bonusSymbolCount() {
        return this._bonusSymbolCount;
    };


    /**
     * Returns array of bonus symbols positions on barrel (only if bonus fired).
     * @returns {Number[]} bonus symbols positions
     * @memberOf SlotMath
     * */
    bonusSymbolPositions() {
        return this._bonusSymbolPositions;
    };

    /**
     * Returns if bonus fired or not.
     * @returns {Boolean} true if bonus fired
     * @memberOf SlotMath
     * */
    hasBonus() {
        return this._bonusSymbolCount >= this._bonusWinCondition;
    };

    /**
     * Returns win for spin.
     * @returns {Number} Win in credits
     * @memberOf SlotMath
     * */
    win() {
        return this.linesWin() + this.scatterWin();
    };

    /**
     * Returns array of win symbol positions on the barrel.
     * @returns {Number[]} Array of symbol positions
     * @memberOf SlotMath
     * */
    winSymbolsPositions() {
        var winPositions = [];

        if (this.linesWin() > 0) {
            for (var i = 0; i < this._actualLines; ++i) {
                if (this._linesWinPositions[i] == null) continue;
                for (var j = 0; j < this._linesWinPositions[i].length; ++j) {
                    var idx = this._linesWinPositions[i][j];
                    winPositions.push(idx);
                }
            }
        }

        if (this.scatterWin() > 0) {
            for (var i = 0; i < this._scatterSymbolCount; ++i) {
                var idx = this._scatterSymbolPositions[i];
                winPositions.push(idx);
            }
        }

        if (this._bonusSymbol !== -1 && this.hasBonus()) {
            for (var i = 0; i < this._bonusSymbolCount; ++i) {
                var idx = this._bonusSymbolPositions[i];
                winPositions.push(idx);
            }
        }

        function onlyUnique(value, index, self) {
            return self.indexOf(value) === index;
        }

        return winPositions.filter(onlyUnique);
    };

    /**
     * Returns array of slot symbol.
     * @returns {Number[]} Array of symbol
     * @memberOf SlotMath
     * */
    slotSymbols() {
        return this._combination;
    };


    /**
     * Parse line symbols combination
     * @param lineCombination {Number[]} Array of symbols on line
     * @returns {Object} Info object
     */
    parseSymbols(lineCombination) {

        // to be overridden - here you can make decision how to parse
        // for example - if playing special win in freegames - you can
        // call here your own parsing method


        // but here by default is checking combination with all possible win patterns and max win is selected

        var results = [];

        for ( const linePattern of this._linePatterns )
        {
            var begin = 0;
            var length = 0;
            for (var j = 0; j < this._reels; ++j) {
                if ( linePattern[ j ] == 1 ) {
                    length++;
                }
                else {
                    // pattern has been already began and now is broken
                    if (length > 0) {
                        break;
                    }
                    // pattern has not yet been began
                    else {
                        begin++;
                    }
                }
            }

            const winResult = this.parseRegularSymbols(
                lineCombination.slice( begin, begin + length ),
                this._wildSymbol,
                this._wildSymbolLineWinMultiplier,
                this._scatterSymbol,
                this._bonusSymbol
            )
            
            if (winResult) {
                winResult.winSymbolIndex = winResult.winSymbolIndex.map( x => x + begin )
                results.push(winResult);
            }
        }

        results.sort( ( a, b ) => b.win - a.win )

        if (results.length === 0) {
            return null;
        }

        return results[ 0 ]
    };


    /**
    * Parse line symbols combination - common algorithm
    * @param lineCombination {Number[]} Array of symbols on line
    * @returns {Object} Info object
    */
    parseRegularSymbols(
        lineCombination,
        wildSymbol,
        wildSymbolLineWinMultiplier,
        scatterSymbol,
        bonusSymbol
    )
    {
        wildSymbol = wildSymbol != null ? wildSymbol : -1;
        wildSymbolLineWinMultiplier = wildSymbolLineWinMultiplier != null ? wildSymbolLineWinMultiplier : 1;
        scatterSymbol = scatterSymbol != null ? scatterSymbol : 1;
        bonusSymbol = bonusSymbol != null ? bonusSymbol : 1;

        if (wildSymbol !== scatterSymbol && wildSymbol !== bonusSymbol && (lineCombination[0] === scatterSymbol || lineCombination[0] === bonusSymbol)) {
            return null;
        }

        var winSymbolIndex = [];

        // символ с начала линии
        var currentSymbol = lineCombination[0];

        winSymbolIndex.push(0);

        var combinationLength = 1;

        var hasWild = (currentSymbol === wildSymbol);

        var wildsCombinationLength = hasWild ? 1 : 0;

        //этот алгоритм написан так что подразумевается что выигрышные комбинации могут начинаться только слева траекторий, но заканчиваться где угодно дальше справа, однако серверный winShapes подразумевает что линия должна являться точным соответствием одной из приведённой комбинации И слева И справа:
        for (var i = 1; i < lineCombination.length; ++i) {
            // текущий символ не изменился
            if (lineCombination[i] === currentSymbol) {

                if (currentSymbol === wildSymbol) {
                    wildsCombinationLength++;
                }

                combinationLength++;

                winSymbolIndex.push(i);
            }
            // текущий символ изменился
            else {

                if (wildSymbol !== -1 && wildSymbol !== scatterSymbol && wildSymbol !== bonusSymbol) {
                    // если цепочка прервана нелинейным символом
                    if (lineCombination[i] === scatterSymbol || lineCombination[i] === bonusSymbol) {
                        //любое несоответствие winShapes прерывает поиск:
                        /*// если оборвалась цепочка первоначально собранная из wild
                        if (currentSymbol === wildSymbol) {
                            hasWild = false; // чисто wild цепочка не содержит признак по которому идет возможно умножения выигрыша на линии
                        }
                        
                        break;*/
                        return null
                    }
                }

                // если оборвалась цепочка первоначально собранная из wild
                if (currentSymbol === wildSymbol) {
                    // дальше может быть обычный (не scatter и не бонус) символ и потом могут быть опять wild

                    const symbol = lineCombination[i];

                    if (!this.symbolSubstitutedByWild(symbol)) {
                        //любое несоответствие winShapes прерывает поиск:
                        /*hasWild = false;
                        break;*/
                        return null
                    }

                    combinationLength++;

                    winSymbolIndex.push(i);

                    // сканируем тут до конца линиии и ищем или symbol или wild - любой другой символ заканчивает комбинацию
                    for (var j = i + 1; j < lineCombination.length; ++j) {

                        if (lineCombination[j] === symbol ||
                            lineCombination[j] === wildSymbol) {
                            combinationLength++;

                            winSymbolIndex.push(j);
                        }
                        else {
                            //любое несоответствие winShapes прерывает поиск:
                            //break;
                            return null
                        }
                    }

                    // console.log("WILDS x " + wildsCombinationLength + " = " + this.price(wildSymbol, wildsCombinationLength));
                    // console.log("" + symbol + " x " + combinationLength + " = " + (this.price(symbol, combinationLength) * (hasWild ? wildSymbolLineWinMultiplier : 1)));

                    if (this._price[wildSymbol][wildsCombinationLength] >= (this._price[symbol][combinationLength] * (hasWild ? wildSymbolLineWinMultiplier : 1))) {

                        if (wildsCombinationLength < combinationLength) {
                            winSymbolIndex = winSymbolIndex.splice(0, wildsCombinationLength);
                        }

                        combinationLength = wildsCombinationLength;
                        hasWild = false; // специально отмечаем чтобы выигрыш за комбинацию только из wild не умножать на специальный множитель
                    }
                    else {
                        currentSymbol = symbol;
                    }

                    break;
                }
                // цепочка была из обычных символов и попался wild
                else {

                    if (lineCombination[i] === wildSymbol) {

                        if (!this.symbolSubstitutedByWild(currentSymbol)) {
                            //любое несоответствие winShapes прерывает поиск:
                            //break;
                            return null
                        }

                        hasWild = true;
                        combinationLength++;
                        winSymbolIndex.push(i);
                    }
                    //  цепочка прервалась (попался другой символ и не wild)
                    else {
                        //любое несоответствие winShapes прерывает поиск:
                        //break;
                        return null
                    }
                }
            }
        }

        var lineWin = 0;

        if (currentSymbol === wildSymbol){

            lineWin = this._price[currentSymbol][combinationLength];

            // если выигрыш за wild по таблице ничего не приносит, попытаемся заменить цепочку из wild на самые дорогие
            if (lineWin === 0) {

                // ищем самый дорогой
                var elementId = 0;
                var maxPrice = this._price[elementId][combinationLength];

                for (var i = 1 ; i < this._symbols; ++i) {
                    var price = this._price[i][combinationLength];
                    if (price > maxPrice) {
                        maxPrice = price;
                        elementId = i;
                    }
                }

                hasWild = false;
                lineWin = maxPrice;
                currentSymbol = elementId;
            }
        }
        else {
            lineWin = this._price[currentSymbol][combinationLength] * (hasWild ? this._wildSymbolLineWinMultiplier : 1);
        }

        if (lineWin === 0) return null;

        return {
            winSymbol: currentSymbol,
            winSymbolIndex:winSymbolIndex,
            combinationLength: combinationLength,
            hasWild: hasWild,
            win: lineWin
        };
    };

    /**
     * Checks if symbol is substitutable by WILD or not.
     * @returns {Boolen} true is substitutable
     * @memberOf SlotMath
     * */
    symbolSubstitutedByWild(symbol) {
        return this._substitutableElements.includes(symbol);
    }

    symbolsSubstitutedByWild(symbol) {
        return this.symbolSubstitutedByWild(symbol);
    }


    checkScatterSymbol(combination, specialSymbol){

        var symbolPositions = [];

        for (var i = 0; i < combination.length; ++i) {
            if (combination[i] == specialSymbol) {
                symbolPositions.push(i);
            }
        }

        return (symbolPositions.length > 0) ? symbolPositions.slice() : symbolPositions;
    }


     /**
     * Calculates win components. This is class core method.
     * @param {Number[]} combination Slot combination array
     * @param {Number} lines Actual lines number
     * @param {Number} multiplier Actual multiplier value
     * @memberOf SlotMath
     * */
    spin(combination, lines, multiplier, winMultiplier) {

        this._combination = combination.concat();

        this._actualLines = lines;
        this._actualMultiplier = multiplier || 1;

        this._winMultiplier = winMultiplier || 1;

        if (this._scatterSymbol !== -1) {
            this._scatterSymbolPositions = this.checkScatterSymbol(combination, this._scatterSymbol);
            this._scatterSymbolCount = this._scatterSymbolPositions.length;
        }

        if (this._bonusSymbol !== -1) {
            this._bonusSymbolPositions = this.checkScatterSymbol(combination, this._bonusSymbol);
            this._bonusSymbolCount = this._bonusSymbolPositions.length;
        }

        this._linesWin = [];
        this._linesWinPositions = [];

        this._lineWinSymbols = [];

        for (var line = 0; line < this._actualLines; ++line) {

            // getting all line symbols
            var lineCombination = [];
            for (var j = 0; j < this._reels; ++j) {
                var symbol = combination[j * this._rows + this._lineTrajectories[line][j]];
                lineCombination.push(symbol);
            }

            var lineWin = 0;

            var result = this.parseSymbols(lineCombination);
            if (result) {
                // only wild symbols on line  don't multiply line win
                if (result.winSymbol === this._wildSymbol) {
                    lineWin = this._price[result.winSymbol][result.combinationLength];
                }
                else {
                    lineWin = this._price[result.winSymbol][result.combinationLength] * (result.hasWild ? this._wildSymbolLineWinMultiplier : 1);
                }
            }

            this._linesWin.push(lineWin);

            if (lineWin > 0) {
                var winPositions = [];
                for (var j = 0; j < result.combinationLength; ++j) {
                    var column = result.winSymbolIndex[j];
                    winPositions.push(this._lineTrajectories[line][column] + column * this._rows);
                }

                this._linesWinPositions.push(winPositions);
                this._lineWinSymbols.push(result.winSymbol);
            }
            else {
                this._linesWinPositions.push(null);
                this._lineWinSymbols.push(null);
            }
        }
    };

    /**
     * Calculates win components for free games.
     * @param {Number[]} combination Slot combination array
     * @param {Number} lines Actual lines number
     * @param {Number} multiplier Actual multiplier value
     * @memberOf SlotMath
     * */
    freespin(combination, lines, multiplier, freespinMultiplier) {

        // тут возможно могут быть изменены занчения множителей, специфичные для бесплатных игр

        this.spin(combination, lines, multiplier, freespinMultiplier);
    }
}




// WEBPACK FOOTER //
// ./src/library/slot-math.es6